import { useEffect, useState } from 'react'
import {
  loadDebtorsCreditors,
  DebtorsCreditorsReport,
  AGE_BANDS,
  AGE_BAND_LABEL,
} from '../../services/ledgersService'
import { CREDIT_STATUS_LABEL } from '../../services/buyerService'
import ReportRangeFilter from './ReportRangeFilter'
import { ALL_TIME, DateRange } from '../../lib/dateRange'

/**
 * §6.4 — the creditor and debtor report.
 *
 * Debtor information existed in four places and was aggregated in none of them:
 * the AR reminder table listed invoices, the invoice ledger listed invoices,
 * the dashboard had a single total, and nothing anywhere was aged. The creditor
 * side did not exist at all.
 *
 * Aged on the due date, which is the only ageing an accountant will accept —
 * ageing on the invoice date makes a 14-day term look a fortnight overdue.
 */

const money = (n: number) =>
  `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function DebtorsCreditorsTab() {
  // Debtors age on the invoice due date, creditors on when the deal was paid.
  // The window applies to both, so "what was owed over August" is one question
  // with one answer rather than two screens.
  const [range, setRange] = useState<DateRange>(ALL_TIME)
  return (
    <>
      <ReportRangeFilter value={range} onChange={setRange} />
      <DebtorsCreditorsBody key={`${range.from}|${range.to}`} range={range} />
    </>
  )
}

function DebtorsCreditorsBody({ range }: { range: DateRange }) {
  const [rep, setRep] = useState<DebtorsCreditorsReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    loadDebtorsCreditors(range)
      .then((r) => !cancelled && setRep(r))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Could not load the ledger'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) return <div className="rep-loading">Loading debtors and creditors…</div>
  if (error) return <div className="rep-alert error">{error}</div>
  if (!rep) return null

  const exportCsv = () => {
    const head = ['Buyer', 'Credit status', 'Risk', 'Invoices', 'Outstanding', ...AGE_BANDS.map((b) => AGE_BAND_LABEL[b]), 'Oldest days']
    const lines = rep.debtors.map((d) =>
      [
        `"${d.buyer.replace(/"/g, '""')}"`,
        d.creditStatus,
        d.riskRating,
        d.invoices,
        d.outstanding.toFixed(2),
        ...AGE_BANDS.map((b) => d.bands[b].toFixed(2)),
        d.oldestDays,
      ].join(',')
    )
    const blob = new Blob(['﻿' + [head.join(','), ...lines].join('\r\n')], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `DirectAuto_AgedDebtors_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="rep-tabbody">
      <div className="rep-stats">
        <div className="rep-stat">
          <span className="rep-stat-label">Total debtors</span>
          <span className="rep-stat-value">{money(rep.debtorTotals.outstanding)}</span>
          <span className="rep-stat-note">{rep.debtorTotals.invoices} open invoices</span>
        </div>
        <div className="rep-stat">
          <span className="rep-stat-label">Over 30 days</span>
          <span className="rep-stat-value">{money(rep.debtorTotals.bands.d30_plus)}</span>
          <span className="rep-stat-note">the collections band</span>
        </div>
        <div className="rep-stat">
          <span className="rep-stat-label">Total creditors</span>
          <span className="rep-stat-value">{money(rep.creditorTotals.owing)}</span>
          <span className="rep-stat-note">{rep.creditorTotals.vehicles} vehicles committed, not yet paid</span>
        </div>
        <div className="rep-stat">
          <span className="rep-stat-label">Net position</span>
          <span className="rep-stat-value">
            {money(rep.debtorTotals.outstanding - rep.creditorTotals.owing)}
          </span>
          <span className="rep-stat-note">debtors less creditors</span>
        </div>
      </div>

      <div className="rep-card">
        <div className="rep-card-head">
          <h3>Aged debtors</h3>
          <button className="rep-btn" onClick={exportCsv}>Export CSV</button>
        </div>
        <p className="rep-note">
          Aged on the due date rather than the invoice date, so a vehicle sold on 14-day terms is
          only overdue once those 14 days have passed.
        </p>
        {rep.debtors.length === 0 ? (
          <div className="rep-alert">Nothing outstanding — every issued invoice has been settled.</div>
        ) : (
          <div className="rep-table-wrap">
            <table className="rep-table">
              <thead>
                <tr>
                  <th>Buyer</th>
                  <th>Standing</th>
                  <th className="num">Invoices</th>
                  {AGE_BANDS.map((b) => (
                    <th key={b} className="num">{AGE_BAND_LABEL[b]}</th>
                  ))}
                  <th className="num">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {rep.debtors.map((d) => (
                  <tr key={d.buyerId || d.buyer}>
                    <td>{d.buyer}</td>
                    <td>
                      <span className={`rep-chip credit-${d.creditStatus}`}>
                        {CREDIT_STATUS_LABEL[d.creditStatus as keyof typeof CREDIT_STATUS_LABEL] || d.creditStatus}
                      </span>
                      {d.riskRating !== 'unrated' && (
                        <span className={`rep-chip risk-${d.riskRating}`}>{d.riskRating} risk</span>
                      )}
                    </td>
                    <td className="num">{d.invoices}</td>
                    {AGE_BANDS.map((b) => (
                      <td key={b} className={`num${b === 'd30_plus' && d.bands[b] > 0 ? ' rep-bad' : ''}`}>
                        {d.bands[b] > 0 ? money(d.bands[b]) : '—'}
                      </td>
                    ))}
                    <td className="num"><strong>{money(d.outstanding)}</strong></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  {AGE_BANDS.map((b) => (
                    <td key={b} className="num">{money(rep.debtorTotals.bands[b])}</td>
                  ))}
                  <td className="num"><strong>{money(rep.debtorTotals.outstanding)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="rep-card">
        <div className="rep-card-head">
          <h3>Creditors</h3>
        </div>
        <p className="rep-note">
          What is owed to sellers — vehicles that are committed or approved for payment but where
          the money has not yet left the bank.
        </p>
        {rep.creditors.length === 0 ? (
          <div className="rep-alert">Nothing owed to sellers right now.</div>
        ) : (
          <div className="rep-table-wrap">
            <table className="rep-table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th className="num">Vehicles</th>
                  <th className="num">Oldest (days)</th>
                  <th className="num">Owing</th>
                </tr>
              </thead>
              <tbody>
                {rep.creditors.map((c) => (
                  <tr key={c.supplier}>
                    <td>{c.supplier}</td>
                    <td className="num">{c.vehicles}</td>
                    <td className="num">{c.oldestDays}</td>
                    <td className="num"><strong>{money(c.owing)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
